/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * FREQUENCY BARS — The "Hello World" of Audio Visualization
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * This is the primary learning reference for building VoteBeats visualizations.
 * It maps FFT frequency data to vertical bars — the most fundamental audio
 * visualization technique.
 *
 * HOW IT WORKS:
 * 1. The AudioAnalyzer gives us `frequencyData` — a Uint8Array of 1024 values (0-255).
 *    Each value represents the amplitude of a frequency bin.
 *    Low indices = bass frequencies, high indices = treble.
 *
 * 2. We divide the canvas into N bars (e.g., 64).
 *    Each bar represents a chunk of frequency bins.
 *    Bar height = average amplitude of its bins, normalized to canvas height.
 *
 * 3. Color: We use HSL color — hue shifts across the bars so bass = warm (red/orange)
 *    and treble = cool (blue/purple). Brightness pulses on beats.
 *
 * TUNABLE PARAMETERS (exposed to DJ via getSettings):
 *   - barCount: How many bars to draw (fewer = chunkier, more = detailed)
 *   - gap: Space between bars (0 = no gap, solid wall)
 *   - colorHue: Starting hue offset (shift the entire color palette)
 *   - sensitivity: Amplitude multiplier (louder/quieter response)
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

export class FrequencyBars implements Visualization {
  id = 'frequency-bars';
  name = 'Frequency Bars';
  category = 'spectrum' as const;
  description = 'Classic vertical bar spectrum analyzer with gradient coloring';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private beatPulse = new BeatPulse(0.93);

  // ── Tunable settings ──────────────────────────────────────────────────────
  private barCount = 64;
  private gap = 2;
  private colorHue = 0;       // hue offset (0 = default warm→cool gradient)
  private sensitivity = 1.2;  // amplitude multiplier

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(frame: FrameData): void {
    const { frequencyData, isBeat, beatIntensity } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;

    // Update beat pulse — spikes on beat, decays each frame
    this.beatPulse.update(isBeat, beatIntensity);

    // Clear the canvas to black (standard for visualizers)
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);

    // ── Calculate bar dimensions ──────────────────────────────────────────
    // Total width minus gaps, divided by number of bars
    const totalGaps = (this.barCount - 1) * this.gap;
    const barWidth = Math.max(1, (width - totalGaps) / this.barCount);

    // ── How many frequency bins per bar ───────────────────────────────────
    // We only use the first ~half of bins (higher bins are often very quiet)
    const usableBins = Math.floor(frequencyData.length * 0.6);
    const binsPerBar = usableBins / this.barCount;

    for (let i = 0; i < this.barCount; i++) {
      // ── Average the frequency bins for this bar ───────────────────────
      const startBin = Math.floor(i * binsPerBar);
      const endBin = Math.floor((i + 1) * binsPerBar);
      let sum = 0;
      for (let b = startBin; b < endBin; b++) {
        sum += frequencyData[b];
      }
      // Normalize: amplitude as fraction of max (255), apply sensitivity
      const amplitude = (sum / (endBin - startBin) / 255) * this.sensitivity;

      // ── Bar height = amplitude * canvas height ────────────────────────
      const barHeight = Math.min(height, amplitude * height);

      // ── Color: hue shifts across the spectrum ─────────────────────────
      // Bass bars (i=0) start at hue 0 (red), treble bars end at hue 240 (blue)
      // Beat pulse adds brightness
      const hue = (i / this.barCount) * 240 + this.colorHue;
      const lightness = 45 + this.beatPulse.value * 25; // 45% base, up to 70% on beat
      ctx.fillStyle = hsl(hue, 80, lightness);

      // ── Draw the bar from the bottom of the canvas ────────────────────
      const x = i * (barWidth + this.gap);
      const y = height - barHeight;
      ctx.fillRect(x, y, barWidth, barHeight);

      // ── Optional: peak hold line (small bright line at the top) ───────
      ctx.fillStyle = hsl(hue, 90, 75, 0.8);
      ctx.fillRect(x, y, barWidth, 2);
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  destroy(): void {
    // Canvas2D doesn't need explicit cleanup
  }

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'barCount', label: 'Bar Count', type: 'range', value: this.barCount, min: 16, max: 128, step: 8 },
      { key: 'gap', label: 'Gap', type: 'range', value: this.gap, min: 0, max: 8, step: 1 },
      { key: 'colorHue', label: 'Color Shift', type: 'range', value: this.colorHue, min: 0, max: 360, step: 10 },
      { key: 'sensitivity', label: 'Sensitivity', type: 'range', value: this.sensitivity, min: 0.5, max: 3, step: 0.1 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.barCount === 'number') this.barCount = settings.barCount;
    if (typeof settings.gap === 'number') this.gap = settings.gap;
    if (typeof settings.colorHue === 'number') this.colorHue = settings.colorHue;
    if (typeof settings.sensitivity === 'number') this.sensitivity = settings.sensitivity;
  }
}
